// Checkout flow for "custom" (own) tours.
// 1. startCustomTourCheckout — resolves pricing for the date, decrements
//    availability atomically, creates a `pending` booking + Stripe Checkout
//    Session (hosted/redirect mode), returns { url, bookingCode }.
// 2. Confirmation reuses /booking/return → confirmBookingFromSession which
//    marks source='custom' bookings as `confirmed` after Stripe says paid.
import { createServerFn } from "@tanstack/react-start";
import { getRequestHost } from "@tanstack/react-start/server";
import { z } from "zod";

const TIERS = ["adult", "child", "infant", "student", "senior", "private", "group"] as const;

const CustomerSchema = z.object({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().max(30).optional().default(""),
  country: z.string().trim().min(2).max(2).default("MX"),
  language: z.string().trim().min(2).max(5).default("es"),
});

const InputSchema = z.object({
  tourId: z.string().uuid(),
  availabilityId: z.string().uuid(),
  items: z
    .array(z.object({ tier: z.enum(TIERS), quantity: z.number().int().min(0).max(50) }))
    .min(1)
    .max(20),
  customer: CustomerSchema,
  environment: z.enum(["sandbox", "live"]),
  /** Selected add-ons. Prices are re-read from the DB, never trusted here. */
  extras: z
    .array(z.object({ id: z.string().uuid(), quantity: z.number().int().min(0).max(50) }))
    .max(30)
    .optional()
    .default([]),
  /** Answers to the tour's own questions. */
  answers: z
    .array(z.object({ questionId: z.string().uuid(), value: z.string().trim().max(500) }))
    .max(40)
    .optional()
    .default([]),
  /** Meeting point / pickup selection (surcharge re-read from the DB). */
  meetingPointId: z.string().uuid().nullable().optional().default(null),
  hotelInfo: z
    .object({
      name: z.string().trim().max(160).optional().default(""),
      address: z.string().trim().max(240).optional().default(""),
      room: z.string().trim().max(40).optional().default(""),
      flightNumber: z.string().trim().max(40).optional().default(""),
      arrivalTime: z.string().trim().max(40).optional().default(""),
      notes: z.string().trim().max(400).optional().default(""),
    })
    .optional()
    .default({}),
  termsAccepted: z.boolean().optional().default(false),
});

function originFromHost(): string {
  try {
    const host = getRequestHost();
    if (!host) return "http://localhost:3000";
    const proto = host.includes("localhost") ? "http" : "https";
    return `${proto}://${host}`;
  } catch {
    return "http://localhost:3000";
  }
}

export interface StartCustomCheckoutResult {
  ok: boolean;
  url: string | null;
  bookingCode: string | null;
  error: string | null;
}

export const startCustomTourCheckout = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => InputSchema.parse(d))
  .handler(async ({ data }): Promise<StartCustomCheckoutResult> => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      // Resolve tour
      const { data: tour, error: tErr } = await supabaseAdmin
        .from("custom_tours")
        .select(
          "id, title, slug, min_participants, max_participants, booking_cutoff_hours, terms, requires_hotel_info, requires_flight_info",
        )
        .eq("id", data.tourId)
        .eq("active", true)
        .is("deleted_at", null)
        .maybeSingle();
      if (tErr || !tour) {
        return { ok: false, url: null, bookingCode: null, error: "Tour no disponible." };
      }

      // Resolve availability slot
      const { data: slot, error: sErr } = await supabaseAdmin
        .from("custom_tour_availability")
        .select("id, date, start_time, capacity, sold, status")
        .eq("id", data.availabilityId)
        .eq("tour_id", data.tourId)
        .maybeSingle();
      if (sErr || !slot || slot.status !== "open") {
        return { ok: false, url: null, bookingCode: null, error: "El horario seleccionado no está disponible." };
      }

      const totalPax = data.items.reduce((s, i) => s + i.quantity, 0);
      if (totalPax <= 0) {
        return { ok: false, url: null, bookingCode: null, error: "Selecciona al menos 1 viajero." };
      }
      if (slot.capacity - slot.sold < totalPax) {
        return { ok: false, url: null, bookingCode: null, error: "No hay cupo suficiente." };
      }

      // Booking rules (server is the only authority) -----------------------
      const minPax = Number(tour.min_participants ?? 0) || 0;
      const maxPax = Number(tour.max_participants ?? 0) || 0;
      if (minPax > 0 && totalPax < minPax) {
        return { ok: false, url: null, bookingCode: null, error: `Esta experiencia requiere al menos ${minPax} viajeros.` };
      }
      if (maxPax > 0 && totalPax > maxPax) {
        return { ok: false, url: null, bookingCode: null, error: `El máximo por reserva es de ${maxPax} viajeros.` };
      }

      const cutoffHours = Number(tour.booking_cutoff_hours ?? 0) || 0;
      if (cutoffHours > 0) {
        const startsAt = new Date(`${slot.date}T${slot.start_time ?? "00:00:00"}`);
        if (Number.isFinite(startsAt.getTime()) && startsAt.getTime() - Date.now() < cutoffHours * 3600_000) {
          return {
            ok: false,
            url: null,
            bookingCode: null,
            error: `Las reservas cierran ${cutoffHours} h antes de la salida. Elige otra fecha u horario.`,
          };
        }
      }

      if (tour.terms && !data.termsAccepted) {
        return { ok: false, url: null, bookingCode: null, error: "Debes aceptar los términos y condiciones." };
      }

      const hotel = data.hotelInfo ?? {};
      if (tour.requires_hotel_info && !(hotel.name ?? "").trim()) {
        return { ok: false, url: null, bookingCode: null, error: "Indica el hotel donde te hospedas." };
      }
      if (tour.requires_flight_info && !(hotel.flightNumber ?? "").trim()) {
        return { ok: false, url: null, bookingCode: null, error: "Indica el número de vuelo." };
      }

      // Required questions --------------------------------------------------
      const { data: questionRows } = await supabaseAdmin
        .from("custom_tour_questions")
        .select("id, label, required, active")
        .eq("tour_id", data.tourId)
        .eq("active", true);
      const answerMap = new Map(data.answers.map((a) => [a.questionId, a.value.trim()]));
      for (const q of questionRows ?? []) {
        if (q.required && !answerMap.get(q.id)) {
          return { ok: false, url: null, bookingCode: null, error: `Falta responder: ${q.label}` };
        }
      }
      const answers = (questionRows ?? [])
        .filter((q) => answerMap.get(q.id))
        .map((q) => ({ question_id: q.id, label: q.label, value: answerMap.get(q.id) as string }));

      // Extras: prices and rules always re-read from the database ----------
      const { data: extraRows } = await supabaseAdmin
        .from("custom_tour_extras")
        .select("id, title, price, currency, charge_type, required, max_quantity, active")
        .eq("tour_id", data.tourId)
        .eq("active", true);
      const requestedExtras = new Map(
        data.extras.filter((e) => e.quantity > 0).map((e) => [e.id, e.quantity]),
      );
      const extrasBreakdown: {
        extra_id: string; title: string; quantity: number; unit_price: number;
        charge_type: string; line_total: number;
      }[] = [];
      for (const ex of extraRows ?? []) {
        let qty = requestedExtras.get(ex.id) ?? 0;
        if (ex.required && qty <= 0) qty = ex.charge_type === "per_person" ? totalPax : 1;
        if (qty <= 0) continue;
        const max = Number(ex.max_quantity ?? 0) || 0;
        if (max > 0 && qty > max) {
          return { ok: false, url: null, bookingCode: null, error: `Máximo ${max} de "${ex.title}".` };
        }
        const unit = Number(ex.price ?? 0) || 0;
        const units = ex.charge_type === "per_person" ? qty * totalPax : qty;
        extrasBreakdown.push({
          extra_id: ex.id,
          title: ex.title,
          quantity: qty,
          unit_price: unit,
          charge_type: ex.charge_type,
          line_total: Number((unit * units).toFixed(2)),
        });
        requestedExtras.delete(ex.id);
      }
      if (requestedExtras.size > 0) {
        return { ok: false, url: null, bookingCode: null, error: "Un extra seleccionado ya no está disponible." };
      }
      const extrasTotal = extrasBreakdown.reduce((s, e) => s + e.line_total, 0);

      // Meeting point / pickup surcharge ------------------------------------
      let pickupSurcharge = 0;
      let pickupPointId: string | null = null;
      let pickupName: string | null = null;
      if (data.meetingPointId) {
        const { data: mp } = await supabaseAdmin
          .from("custom_tour_meeting_points")
          .select("id, name, extra_price, active")
          .eq("id", data.meetingPointId)
          .eq("tour_id", data.tourId)
          .maybeSingle();
        if (!mp || mp.active === false) {
          return { ok: false, url: null, bookingCode: null, error: "El punto de encuentro seleccionado no está disponible." };
        }
        pickupPointId = mp.id;
        pickupName = mp.name;
        pickupSurcharge = Number(mp.extra_price ?? 0) || 0;
      }

      // Resolve pricing: season or base
      const { data: seasons } = await supabaseAdmin
        .from("custom_tour_seasons")
        .select("id, priority")
        .eq("tour_id", data.tourId)
        .eq("active", true)
        .lte("start_date", slot.date)
        .gte("end_date", slot.date)
        .order("priority", { ascending: false })
        .limit(1);

      const prices: Record<string, number> = {};
      let currency = "MXN";
      const season = seasons?.[0];
      if (season) {
        const { data: sp } = await supabaseAdmin
          .from("custom_tour_season_pricing")
          .select("tier, base_price, currency")
          .eq("season_id", season.id);
        for (const r of sp ?? []) {
          prices[r.tier as string] = Number(r.base_price);
          currency = r.currency ?? currency;
        }
      }
      if (Object.keys(prices).length === 0) {
        const { data: bp } = await supabaseAdmin
          .from("custom_tour_pricing")
          .select("tier, base_price, currency")
          .eq("tour_id", data.tourId);
        for (const r of bp ?? []) {
          prices[r.tier as string] = Number(r.base_price);
          currency = r.currency ?? currency;
        }
      }

      const breakdown = data.items
        .filter((i) => i.quantity > 0)
        .map((i) => ({
          tier: i.tier,
          quantity: i.quantity,
          unit_price: prices[i.tier] ?? 0,
          line_total: (prices[i.tier] ?? 0) * i.quantity,
        }));
      const paxSubtotal = breakdown.reduce((s, b) => s + b.line_total, 0);
      if (paxSubtotal <= 0) {
        return { ok: false, url: null, bookingCode: null, error: "Precio no configurado para esa fecha." };
      }
      // Final amount charged: travellers + add-ons + pickup surcharge.
      const subtotal = Number((paxSubtotal + extrasTotal + pickupSurcharge).toFixed(2));

      // Atomically decrement availability
      const { data: dec, error: decErr } = await supabaseAdmin.rpc("decrement_availability", {
        _avail_id: slot.id,
        _qty: totalPax,
      });
      if (decErr || !dec) {
        return { ok: false, url: null, bookingCode: null, error: "Cupo agotado, intenta otro horario." };
      }

      const reservationExpires = new Date(Date.now() + 30 * 60 * 1000).toISOString();
      // The interface language the traveller booked in travels with the order:
      // booking row -> Stripe metadata -> voucher email.
      const { normalizeLang } = await import("./lang");
      const lang = normalizeLang(data.customer.language);

      const { data: booking, error: insertErr } = await supabaseAdmin
        .from("custom_bookings")
        .insert({
          tour_id: data.tourId,
          tour_title: tour.title,
          customer_name: `${data.customer.firstName} ${data.customer.lastName}`.trim(),
          customer_email: data.customer.email,
          customer_phone: data.customer.phone || null,
          travel_date: slot.date,
          travelers: totalPax,
          currency,
          subtotal: subtotal,
          discount_total: 0,
          total: subtotal,
          status: "pending",
          source: "custom",
          experience_lang: lang,
          availability_id: slot.id,
          reservation_expires_at: reservationExpires,
          extras: extrasBreakdown,
          question_answers: answers,
          pickup_point_id: pickupPointId,
          pickup_surcharge: pickupSurcharge,
          hotel_info: hotel,
          terms_accepted_at: data.termsAccepted ? new Date().toISOString() : null,
          pricing_breakdown: {
            items: breakdown,
            pax_subtotal: Number(paxSubtotal.toFixed(2)),
            extras: extrasBreakdown,
            extras_total: Number(extrasTotal.toFixed(2)),
            pickup_surcharge: pickupSurcharge,
            season_id: season?.id ?? null,
            start_time: slot.start_time,
          },
          metadata: {
            customer: data.customer,
            slot: { date: slot.date, time: slot.start_time },
            pickup_point_name: pickupName,
          },
        })
        .select("id, booking_code")
        .single();

      if (insertErr || !booking) {
        // Release reservation
        await supabaseAdmin.rpc("release_availability", { _avail_id: slot.id, _qty: totalPax });
        return { ok: false, url: null, bookingCode: null, error: insertErr?.message ?? "No se pudo registrar la reserva." };
      }

      const amountCents = Math.max(50, Math.round(subtotal * 100));
      const origin = originFromHost();
      const { createStripeClient, getStripeErrorMessage } = await import("./stripe.server");
      const stripe = createStripeClient(data.environment);

      try {
        const session = await stripe.checkout.sessions.create({
          mode: "payment",
          customer_email: data.customer.email,
          line_items: [
            {
              quantity: 1,
              price_data: {
                currency: currency.toLowerCase(),
                unit_amount: amountCents,
                product_data: {
                  name: tour.title,
                  description: `Reserva ${booking.booking_code} · ${totalPax} pax · ${slot.date} ${slot.start_time ?? ""}`.trim(),
                },
              },
            },
          ],
          metadata: { booking_id: booking.id, booking_code: booking.booking_code, lang },
          payment_intent_data: {
            description: `${tour.title} (${booking.booking_code})`,
            metadata: { booking_id: booking.id, booking_code: booking.booking_code, lang },
          },
          success_url: `${origin}/booking/return?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${origin}/tour/mx-${tour.slug}?cancelled=1`,
        });

        await supabaseAdmin
          .from("custom_bookings")
          .update({ stripe_session_id: session.id })
          .eq("id", booking.id);

        return { ok: true, url: session.url ?? null, bookingCode: booking.booking_code, error: null };
      } catch (stripeErr) {
        // Roll back: release capacity and mark failed
        await supabaseAdmin.rpc("release_availability", { _avail_id: slot.id, _qty: totalPax });
        await supabaseAdmin.from("custom_bookings").update({ status: "failed" }).eq("id", booking.id);
        return {
          ok: false,
          url: null,
          bookingCode: booking.booking_code,
          error: getStripeErrorMessage(stripeErr) || "Stripe rechazó la sesión.",
        };
      }
    } catch (err) {
      return { ok: false, url: null, bookingCode: null, error: (err as Error).message };
    }
  });
